import { jwtDecode } from 'jwt-decode';
import { authHttp, sessionAuth } from './client';
import type { UserProfile } from 'types/auth';

let identity: UserProfile | null = null;
let identityGeneration: string | undefined;
let inFlight: { generation: string; promise: Promise<UserProfile> } | null = null;
const listeners = new Set<(user: UserProfile | null, error?: unknown) => void>();
export function currentIdentity() {
  return sessionAuth.read()?.generation === identityGeneration ? identity : null;
}
export function clearIdentity() {
  identity = null;
  identityGeneration = undefined;
}
export function subscribeIdentity(listener: (user: UserProfile | null, error?: unknown) => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export async function refreshIdentity(force = false): Promise<UserProfile> {
  const session = sessionAuth.read();
  if (!session) throw new Error('Please sign in again.');
  if (inFlight?.generation === session.generation) {
    if (!force) return inFlight.promise;
    await inFlight.promise.catch(() => {});
    if (sessionAuth.read()?.generation !== session.generation) throw new Error('Authentication changed.');
    return refreshIdentity();
  }
  const generation = session.generation;
  const promise = sessionAuth
    .request(token => authHttp.get('/v1/auth/me', { headers: { 'x-access-token': token }, timeout: 15000 }))
    .then(response => {
      if (!response.data.success) throw new Error(response.data.message || 'Unable to load account permissions.');
      if (sessionAuth.read()?.generation !== generation) throw new Error('Authentication changed.');
      const claims = jwtDecode<UserProfile>(sessionAuth.read()!.token);
      // Only /auth/me supplies authorization fields; company roles never become platform roles.
      const display = { ...claims, permissions: [], company_role_id: undefined, company_role_label: undefined };
      identity = { ...display, ...response.data.data, permissions: response.data.data.permissions || [] };
      identityGeneration = generation;
      listeners.forEach(listener => listener(identity));
      return identity!;
    })
    .catch(error => {
      if (sessionAuth.read()?.generation === generation) {
        // Fail closed for company management, while preserving refreshable authentication.
        if (identity) identity = { ...identity, permissions: [] };
        listeners.forEach(listener => listener(identity, error));
      }
      throw error;
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  inFlight = { generation, promise };
  return promise;
}
export function markPermissionsStale() {
  if (identity) {
    identity = { ...identity, permissions: [] };
    listeners.forEach(listener => listener(identity));
  }
  return refreshIdentity(true);
}
let profileChannel: BroadcastChannel | undefined;
try {
  if (typeof BroadcastChannel !== 'undefined') profileChannel = new BroadcastChannel('hackerest-profile');
} catch {
  /* Storage events are the fallback. */
}
export function notifyIdentityChanged() {
  const generation = sessionAuth.read()?.generation;
  profileChannel?.postMessage({ generation });
  localStorage.setItem('profileRevision', JSON.stringify({ generation, revision: crypto.randomUUID() }));
}
function receiveChange(generation?: string) {
  if (generation === sessionAuth.read()?.generation) void markPermissionsStale().catch(() => {});
}
if (profileChannel) profileChannel.onmessage = event => receiveChange(event.data?.generation);
window.addEventListener('storage', event => {
  if (event.key === 'profileRevision' && event.newValue) {
    try {
      receiveChange(JSON.parse(event.newValue).generation);
    } catch {
      /* Ignore malformed notifications. */
    }
  }
});
